import React from 'react'
import { Link } from 'react-router'
import { ChevronRightIcon, CheckIcon } from '@heroicons/react/20/solid'

// Marketplace action-column cell. Surfaces a listing's existing Deals /
// Follow-ups plus the right next action (Express Interest, add from another
// requirement, or the terminal "all engaged" state). Shared by carbon and ppa;
// the only per-app difference is the requirement abbreviation (RR vs PR) and the
// "no eligible requirement" copy, both passed in as props.

export interface InterestRecord {
  kind: 'deal' | 'follow_up'
  deal_id?: string
  follow_up_id?: string
  status: string
  requirement_id: string
  requirement_name: string | null
  created_at: string
}

export interface InterestCellProps {
  interests: InterestRecord[]
  channel: 'in_market' | 'external'
  totalEligibleCount: number
  scopedRequirementId?: string
  onExpress: () => void
  // Requirement abbreviation used in the engagement labels ('RR' for carbon
  // reduction requirements, 'PR' for ppa power requirements).
  requirementAbbrev: string
  // Tooltip shown on the disabled CTA when the user has no eligible requirement.
  noEligibleMessage: string
}

const VISIBLE_MAX = 4

export default function InterestCell(props: InterestCellProps) {
  const {
    interests,
    channel,
    totalEligibleCount,
    scopedRequirementId,
    onExpress,
    requirementAbbrev,
    noEligibleMessage,
  } = props

  const plural = (count: number) => (count === 1 ? requirementAbbrev : `${requirementAbbrev}s`)

  const sorted = React.useMemo(
    () => [...interests].sort((a, b) => +new Date(b.created_at) - +new Date(a.created_at)),
    [interests]
  )
  const visible = sorted.slice(0, VISIBLE_MAX)
  const overflowCount = sorted.length - visible.length

  const n = interests.length
  const engagedFromScope = scopedRequirementId
    ? interests.filter((i) => i.requirement_id === scopedRequirementId).length
    : 0
  const isTerminal = scopedRequirementId
    ? engagedFromScope > 0
    : totalEligibleCount > 0 && n >= totalEligibleCount

  if (n === 0 && totalEligibleCount === 0) {
    return (
      <button
        type="button"
        disabled
        title={noEligibleMessage}
        className="rounded-md bg-gray-300 px-3 py-1.5 text-xs font-medium text-white cursor-not-allowed"
      >
        Express Interest
      </button>
    )
  }

  if (n === 0) {
    // Each button echoes its channel's source-designation badge: in-market is the
    // brand blue (perfect-shine), external the warm amber (anzac-dark). Both are
    // solid fills with white text and equal weight; the deeper anzac-dark keeps the
    // white label crisp where the lighter default gold would wash it out.
    const buttonClass = channel === 'in_market'
      ? 'border-transparent bg-perfect-shine text-white shadow-xs hover:bg-perfect-shine-dark'
      : 'border-transparent bg-anzac-dark text-white shadow-xs hover:bg-anzac-700'
    return (
      <button
        type="button"
        onClick={onExpress}
        className={`rounded-md border px-3 py-1.5 text-xs font-medium ${buttonClass}`}
      >
        Express Interest
      </button>
    )
  }

  return (
    <div className="space-y-1.5">
      {n >= 2 && (
        <div className="text-[11px] text-gray-500">
          {isTerminal
            ? `All ${totalEligibleCount} ${plural(totalEligibleCount)} engaged`
            : `${n} of ${totalEligibleCount} ${plural(totalEligibleCount)} engaged`}
        </div>
      )}
      <ul className="space-y-1">
        {visible.map((interest) => (
          <li key={`${interest.kind}-${interest.deal_id ?? interest.follow_up_id}`}>
            <InterestChip interest={interest} requirementAbbrev={requirementAbbrev} />
          </li>
        ))}
      </ul>
      {overflowCount > 0 && (
        <div className="text-[11px] text-gray-500">+ {overflowCount} more</div>
      )}
      {!isTerminal ? (
        <button
          type="button"
          onClick={onExpress}
          className={`text-[11px] font-medium ${channel === 'in_market' ? 'text-perfect-shine hover:text-perfect-shine-dark' : 'text-gray-600 hover:text-gray-800'} hover:underline`}
        >
          + Add interest from another {requirementAbbrev}
        </button>
      ) : (
        <div className="text-[11px] text-gray-500 italic flex items-center gap-1">
          <CheckIcon className="w-3 h-3 text-perfect-shine-600 shrink-0" />
          {scopedRequirementId ? `Already engaged via this ${requirementAbbrev}` : `All ${totalEligibleCount} ${requirementAbbrev}s engaged`}
        </div>
      )}
    </div>
  )
}

function InterestChip({ interest, requirementAbbrev }: { interest: InterestRecord; requirementAbbrev: string }) {
  const isDeal = interest.kind === 'deal'
  const reqName = interest.requirement_name ?? `Unnamed ${requirementAbbrev}`

  const inner = (
    // Two lines so the chip stays narrow: the requirement name on top, its status + age
    // beneath (aligned under the name), rather than one wide single row.
    <div className="group flex max-w-60 flex-col gap-0.5 rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5 text-xs text-gray-700 transition-colors hover:border-gray-300 hover:bg-gray-100">
      <div className="flex items-center gap-1.5">
        <span className={`shrink-0 text-[10px] ${isDeal ? 'text-perfect-shine-600' : 'text-gray-500'}`}>
          {isDeal ? '◆' : '✉'}
        </span>
        <span className="truncate flex-1 min-w-0 font-medium text-gray-900">{reqName}</span>
        {isDeal && (
          <ChevronRightIcon className="w-3 h-3 shrink-0 text-gray-400 group-hover:text-gray-600" aria-hidden="true" />
        )}
      </div>
      <div className="flex items-center gap-1.5 pl-[18px] text-[10px]">
        <span className={`shrink-0 w-1.5 h-1.5 rounded-full ${statusDotColor(interest.status)}`} aria-hidden="true" />
        <span className="capitalize text-gray-500">{interest.status}</span>
        <span className="text-gray-400 tabular-nums">{formatRelativeAge(interest.created_at)}</span>
      </div>
    </div>
  )

  if (isDeal && interest.deal_id) {
    const href = interest.status === 'consent'
      ? '/app/deals'
      : `/app/deals/${interest.deal_id}`
    return <Link to={href} className="block focus:outline-hidden focus:ring-2 focus:ring-perfect-shine rounded-md">{inner}</Link>
  }
  return inner
}

function statusDotColor(status: string): string {
  if (['consent', 'offered', 'negotiation', 'accepted', 'pending', 'contacted'].includes(status)) return 'bg-anzac'
  if (['ratified', 'closed_won'].includes(status)) return 'bg-perfect-shine'
  if (['withdrawn', 'closed_lost'].includes(status)) return 'bg-gray-400'
  return 'bg-gray-400'
}

function formatRelativeAge(iso: string): string {
  const date = new Date(iso)
  const now = new Date()
  const ms = now.getTime() - date.getTime()
  const minutes = Math.floor(ms / 60_000)
  const hours = Math.floor(ms / 3_600_000)
  const days = Math.floor(ms / 86_400_000)
  if (minutes < 60) return `${Math.max(minutes, 1)}m`
  if (hours < 24) return `${hours}h`
  if (days < 30) return `${days}d`
  if (days < 365) return `${Math.floor(days / 30)}mo`
  return `${Math.floor(days / 365)}y`
}
